/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { PartialySavedFittings } from '../models/PartialySavedFittings';
import { CustomSnackBarComponent } from '../shared/customSnackBar/customSnackBar.component';
import { FittingSizeDataService } from './fittingSize-data.service';


import { UserHandlerService } from './user-handler.service';
import { LoaderService } from './loader.service';
import {
  ActionPageEnumDto, FittingDto, ActionTypeEnumDto, CreatePipeFittingSizeInfoDto,
  CreateElectricalFittingSizeInfoDto, CreateSheetMetalFittingSizeInfoDto, CreateCableTrayFittingSizeInfoDto
} from 'api/models';
import { FittingsManagementService } from 'api/services';
import { GenericMapper } from 'Mappers/GenericMapper';
import { PipingPlumbingFittingSizeMapper } from 'Mappers/PipingPlumbingFittingSizeMapper';
import { ElectricalFittingSizeMapper } from 'Mappers/ElectricalFittingSizeMapper';
import { SheetMetalFittingSizeMapper } from 'Mappers/SheetMetalFittingSizeMapper';
import { CableTrayFittingSizeMapper } from 'Mappers/CableTrayFittingSizeMapper';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class FittingSizeManagementService {
  // tslint:disable-next-line: variable-name
  private readonly _tableDataListSource = new BehaviorSubject([]);
  // tslint:disable-next-line: variable-name
  private readonly _refreshNedded = new Subject<void>();
  // tslint:disable-next-line: variable-name
  private readonly _resetFittingTableSubject: Subject<boolean> = new Subject<boolean>();
  // tslint:disable-next-line: variable-name

  // tslint:disable-next-line: variable-name
  private readonly _partialySavedFittingsSubject = new Subject<PartialySavedFittings>();
  // tslint:disable-next-line: variable-name
  private readonly _addFromExistingFittingSizes = new Subject<void>();
  private actionPage: ActionPageEnumDto;

  currentTableDataList = this._tableDataListSource.asObservable();
  selectedFitting: FittingDto;
  selectedExistingFittingSize: any;
  selectedRowCopy;

  dataType = new GenericMapper();

  constructor(
    private readonly userHandlerService: UserHandlerService,
    private readonly fittingsManagementService: FittingsManagementService,
    private readonly fittingDataService: FittingSizeDataService,
    private readonly snackBar: MatSnackBar,
    private readonly formBuilder: FormBuilder,
    private readonly loaderService: LoaderService) { }

  get refreshNeeded() {
    return this._refreshNedded;
  }

  getActionPage() {
    return this.actionPage;
  }

  setActionPage(actionPage: ActionPageEnumDto) {
    this.actionPage = actionPage;
  }

  // get isLoading() {
  //   return this._isLoading;
  // }

  get getPartialySavedFittings() {
    return this._partialySavedFittingsSubject;
  }
  get getAddedFromExistingFittings() {
    return this._addFromExistingFittingSizes;
  }

  refreshComponent() {
    this._refreshNedded.next();
  }

  getFittingTableData() {
    return this._resetFittingTableSubject;
  }

  resetFittingsTable() {
    this._resetFittingTableSubject.next(true);
  }

  setPartialySavedFittings(value: PartialySavedFittings) {
    this._partialySavedFittingsSubject.next(value);
  }

  addExistingFittingSize() {
    this._addFromExistingFittingSizes.next();
  }

  getTableHeader() {
    return this.selectedFitting.unitID !== undefined ?
      this.dataType.getTableHeaders(this.selectedFitting.unitID) : this.dataType.getTableHeaders(this.selectedFitting[`Unitid`]);
  }

  getSelectedFittingType() {
    return this.selectedFitting ? this.selectedFitting.productDisciplineID : null;
  }

  getSelectedFitting() {
    return this.selectedFitting;
  }

  getSelectedRowCopy() {
    return this.selectedRowCopy;
  }

  getMappedObject(formGroup: FormGroup): GenericMapper {
    return this.dataType.mapToObject(formGroup.controls);
  }
  getMappedFormGroup(baseObject: any): FormGroup {
    return this.dataType.mapToFormGroup(baseObject, this.selectedFitting.unitID, this.actionPage);
  }

  getMappedFormGroupWithExistingValues(nominalSizeList: any, baseObject: any): FormGroup {
    const itemForm = this.getMappedFormGroup(baseObject);
    this.dataType.setValuesByElement(itemForm, baseObject, nominalSizeList, baseObject.isPartialySaved);

    return itemForm;
  }

  getNominalSizesAndAddedFittings() {
    const partialySavedFittings = new PartialySavedFittings();
    partialySavedFittings.PartialySavedFittings = this.formBuilder.array([]);
    this.loaderService.display(true);
    switch (this.selectedFitting.productDisciplineID) {
      case 2: {
        this.fittingsManagementService.getPartialSizePipeFittingSize({
          bdToolId: this.selectedFitting.bdToolID,
          fittingTypeId: this.selectedFitting.fittingTypeID,
          productDisciplineId: this.selectedFitting.productDisciplineID,
          actionTypeEnumDto: this.selectedFitting.statusID,
          isMetric: this.selectedFitting.unitID === 3
        }).subscribe(result => {
          partialySavedFittings.NominalSizeList = result.nominalSizeList;
          result.partialSavedFittingSizes.forEach(item => {
            partialySavedFittings.PartialySavedFittings.push(
              this.getMappedFormGroupWithExistingValues(partialySavedFittings.NominalSizeList, item));
          });
          this.setPartialySavedFittings(partialySavedFittings);
          this.loaderService.display(false);
        });
        break;
      }
      case 4: {
        this.fittingsManagementService.getPartialSizeElectricalFittingSize({
          bdToolId: this.selectedFitting.bdToolID,
          fittingTypeId: this.selectedFitting.fittingTypeID,
          productDisciplineId: this.selectedFitting.productDisciplineID,
          actionTypeEnumDto: this.selectedFitting.statusID,
          isMetric: this.selectedFitting.unitID === 3
        }).subscribe(result => {
          partialySavedFittings.NominalSizeList = result.nominalSizeList;
          result.partialSavedFittingSizes.forEach(item => {
            partialySavedFittings.PartialySavedFittings.push(
              this.getMappedFormGroupWithExistingValues(partialySavedFittings.NominalSizeList, item));
          });
          this.setPartialySavedFittings(partialySavedFittings);
          this.loaderService.display(false);
        });
        break;
      }
      case 5: {
        this.fittingsManagementService.getPartialSizeSheetMetalFittingSize({
          bdToolId: this.selectedFitting.bdToolID,
          fittingTypeId: this.selectedFitting.fittingTypeID,
          productDisciplineId: this.selectedFitting.productDisciplineID,
          actionTypeEnumDto: this.selectedFitting.statusID,
          isMetric: this.selectedFitting.unitID === 3
        }).subscribe(result => {
          partialySavedFittings.NominalSizeList = result.nominalSizeList;
          result.partialSavedFittingSizes.forEach(item => {
            partialySavedFittings.PartialySavedFittings.push(
              this.getMappedFormGroupWithExistingValues(partialySavedFittings.NominalSizeList, item));
          });
          this.setPartialySavedFittings(partialySavedFittings);
          this.loaderService.display(false);
        });
        break;
      }
      case 9: {
        this.fittingsManagementService.getPartialSizeCableTrayFittingSize({
          bdToolId: this.selectedFitting.bdToolID,
          fittingTypeId: this.selectedFitting.fittingTypeID,
          productDisciplineId: this.selectedFitting.productDisciplineID,
          actionTypeEnumDto: this.selectedFitting.statusID,
          isMetric: this.selectedFitting.unitID === 3
        }).subscribe(result => {
          partialySavedFittings.NominalSizeList = result.nominalSizeList;
          result.partialSavedFittingSizes.forEach(item => {
            partialySavedFittings.PartialySavedFittings.push(
              this.getMappedFormGroupWithExistingValues(partialySavedFittings.NominalSizeList, item));
          });
          this.setPartialySavedFittings(partialySavedFittings);
          this.loaderService.display(false);
        });
        break;
      }
    }
  }

  setSelectedRowCopy(value?: any) {
    this.selectedRowCopy = value;
  }

  setTableDataList(data: any[]) {
    if (data === null) {
      data = [];
    }
    this._tableDataListSource.next(data);
  }

  setDefaultValues(elementForm: FormGroup, baseObject: any) {
    this.dataType.setDefaultValues(elementForm, baseObject, this.selectedFitting);
  }

  hasSelectedFitting() {
    return this.selectedFitting ? true : false;
  }
  isFittingDisabled() {
    return this.selectedFitting.isDisabled;
  }

  hasFittingBeenSentToApprove(): boolean {
    if (this.selectedFitting) {
      return this.selectedFitting.isDisabled;
    }
    return;
  }

  setMapperType(fitting: FittingDto) {
    // when changing the fittyng type do not let the onDestroy from component set it to null
    this.selectedFitting = fitting === this.selectedFitting ? this.selectedFitting : fitting;
    if (!this.selectedFitting) {
      return;
    }
    switch (this.selectedFitting.productDisciplineID) {
      case 2: {
        this.dataType = new PipingPlumbingFittingSizeMapper();
        break;
      }
      case 4: {
        this.dataType = new ElectricalFittingSizeMapper();
        break;
      }
      case 5: {
        this.dataType = new SheetMetalFittingSizeMapper();
        break;
      }
      case 9: {
        this.dataType = new CableTrayFittingSizeMapper();
        break;
      }
      default: {
        this.dataType = new GenericMapper();
        break;
      }
    }
  }

  setExistingFittingSize(element: any) {
    this.selectedExistingFittingSize = element;
    this.addExistingFittingSize();
  }

  prepareNewAddedFittings(newFittingSizes: FormArray, actionType: ActionTypeEnumDto) {
    const dataArray = new Array<any>();
    newFittingSizes.controls.forEach((item: FormGroup) => {
      if (item.controls[`isPartialySaved`].value === false || item.controls[`isPartialySaved`].value === null) {
        dataArray.push(this.getMappedObject(item));
      }
    });
    this.executePartialSave(dataArray, actionType);
  }

  executePartialSave(dataArray: any, actionType: ActionTypeEnumDto) {
    this.loaderService.display(true);
    const objectForCreate = this.dataType.createAddSizeObject(
      dataArray,
      this.userHandlerService.getCurrentUserId(),
      this.selectedFitting, actionType);

    switch (this.selectedFitting.productDisciplineID) {
      case 2: {
        this.fittingsManagementService.addNewSizeForPipeFitting({ body: objectForCreate as CreatePipeFittingSizeInfoDto })
          .subscribe(() => {
            this.manageComponentPostAddingNewSize(actionType);


          });
        break;
      }
      case 4: {
        this.fittingsManagementService.addNewSizeForElectricalFitting({ body: objectForCreate as CreateElectricalFittingSizeInfoDto })
          .subscribe(() => {
            this.manageComponentPostAddingNewSize(actionType);

          });
        break;
      }
      case 5: {
        this.fittingsManagementService.addNewSizeForSheetMetalFitting({ body: objectForCreate as CreateSheetMetalFittingSizeInfoDto })
          .subscribe(() => {
            this.manageComponentPostAddingNewSize(actionType);

          });
        break;
      }
      case 9: {
        this.fittingsManagementService.addNewSizeForCableTrayFitting({ body: objectForCreate as CreateCableTrayFittingSizeInfoDto })
          .subscribe(() => {
            this.manageComponentPostAddingNewSize(actionType);

          });
        break;
      }
      default: {
        this.dataType = new GenericMapper();
        break;
      }
    }
  }

  manageComponentPostAddingNewSize(actionType: ActionTypeEnumDto) {
    this.selectedFitting.statusID = actionType;
    this.refreshComponent();
    this.resetFittingsTable();
    this.fittingDataService.refreshComponent();
    // manage in backend
    // this.dataType.isPartialySaved = true;
    this.openSnackBar('Saved Succesfully');
  }

  executeSendForAproval(actionType: ActionTypeEnumDto, comment: string) {
    this.loaderService.display(true);
    this.fittingsManagementService.updateApprovalStatus({
      userId: this.userHandlerService.getCurrentUserId(),
      actionTypeEnumDto: actionType,
      bdToolId: this.selectedFitting.bdToolID,
      fittingEntryId: this.selectedFitting.fittingEntryID,
      comment
    }).subscribe(() => {
      this.selectedFitting.statusID = actionType;
      this.refreshComponent();
      this.resetFittingsTable();
      this.openSnackBar('Sent for aproval');

    });
  }

  removeSavedRecord(elementId, deleteActionType: ActionTypeEnumDto, comment: string) {
    this.loaderService.display(true);
    this.fittingsManagementService.deleteSavedRecord({
      id: elementId,
      userId: this.userHandlerService.getCurrentUserId(),
      bdToolId: this.selectedFitting.bdToolID,
      actionTypeEnumDto: deleteActionType,
      fittingEntryId: this.selectedFitting.fittingEntryID,
      // tslint:disable-next-line: object-literal-shorthand
      comment: comment
    }).subscribe(() => {
      this.openSnackBar('The partial saved record has been removed successfully');
      this.loaderService.display(false);
    });
  }
  openSnackBar(message: string, duration = 3000) {
    this.snackBar.openFromComponent(CustomSnackBarComponent, {
      duration: duration,
      data: message,
  
    });
  }

  openSnackBarWithAction(message: string, action: string) {
    
    this.snackBar.open(message, action, {    
      data: message,
      panelClass: ['custom-snackbar']
    }).onAction().subscribe(() => {
      this.snackBar.dismiss();
    });
  }

}
